const jsonCache = new Map<string, unknown>();
const pendingRequests = new Map<string, Promise<unknown>>();
const preloadedPaths = new Set<string>();

export function getCachedJson<T>(path: string): T | undefined {
  return jsonCache.get(path) as T | undefined;
}

export function preloadJson(path: string) {
  if (preloadedPaths.has(path) || jsonCache.has(path)) {
    return;
  }

  preloadedPaths.add(path);
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "fetch";
  link.href = path;
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
}

export async function fetchStaticJson<T>(path: string): Promise<T> {
  const cached = jsonCache.get(path) as T | undefined;

  if (cached) {
    return cached;
  }

  const pending = pendingRequests.get(path) as Promise<T> | undefined;

  if (pending) {
    return pending;
  }

  const request = fetch(path, {
    headers: { Accept: "application/json" },
  })
    .then(async (response) => {
      const data = (await response.json().catch(() => null)) as T | null;

      if (!response.ok || !data) {
        throw new Error(`Failed to load ${path}`);
      }

      jsonCache.set(path, data);
      return data;
    })
    .finally(() => {
      pendingRequests.delete(path);
    });

  pendingRequests.set(path, request);

  return request;
}
